export const CUSTOM_LABEL_BETA_STORAGE_KEY = "assistco-custom-label-product-beta-v1";

export const CUSTOM_LABEL_SHEET = Object.freeze({
  widthMm: 200,
  heightMm: 307,
  headerMm: 22,
  marginMm: 5,
  gapMm: 0.5,
});

export const CUSTOM_LABEL_SIZE_PRESETS = Object.freeze([
  { id: "tiny", name: "Mini", widthMm: 16, heightMm: 6, note: "Små ting og penner" },
  { id: "classic", name: "Klassisk", widthMm: 30, heightMm: 13, note: "Klær og hverdagsutstyr", popular: true },
  { id: "medium", name: "Mellomstor", widthMm: 45, heightMm: 18, note: "Matbokser og flasker" },
  { id: "large", name: "Stor", widthMm: 60, heightMm: 25, note: "Sekker og sportsutstyr" },
]);

export const CUSTOM_LABEL_QUANTITY_PRESETS = Object.freeze([24, 48, 96, 120, 180]);

const DEFAULT_DRAFT = Object.freeze({
  version: 1,
  status: "private-beta",
  productHandle: "",
  productBindingId: "",
  widthMm: 30,
  heightMm: 13,
  bleedMm: 0.5,
  gapMm: 0.5,
  quantity: 120,
  shape: "rounded",
  cornerRadiusMm: 3,
  primaryText: "Emma Nordli",
  secondaryText: "90000000",
  thirdText: "",
  motifMode: "library",
  iconId: "",
  uploadedImage: null,
  fontId: "",
  backgroundColorId: "",
  textColorId: "",
  alignment: "left",
});

function numberInRange(value, fallback, min, max) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
}

export function createCustomLabelBetaDraft(input = {}) {
  return {
    ...DEFAULT_DRAFT,
    ...input,
    version: 1,
    status: "private-beta",
    productHandle: "",
    productBindingId: "",
    widthMm: numberInRange(input.widthMm, DEFAULT_DRAFT.widthMm, 10, 130),
    heightMm: numberInRange(input.heightMm, DEFAULT_DRAFT.heightMm, 5, 80),
    bleedMm: numberInRange(input.bleedMm, DEFAULT_DRAFT.bleedMm, 0, 3),
    gapMm: numberInRange(input.gapMm, DEFAULT_DRAFT.gapMm, 0, 5),
    quantity: Math.round(numberInRange(input.quantity, DEFAULT_DRAFT.quantity, 1, 2_000)),
    uploadedImage: input.uploadedImage?.dataUrl ? input.uploadedImage : null,
  };
}

function orientationLayout({ widthMm, heightMm, bleedMm, gapMm }, rotated = false) {
  const labelWidth = rotated ? heightMm : widthMm;
  const labelHeight = rotated ? widthMm : heightMm;
  const printableWidth = CUSTOM_LABEL_SHEET.widthMm - CUSTOM_LABEL_SHEET.marginMm * 2;
  const printableHeight = CUSTOM_LABEL_SHEET.heightMm - CUSTOM_LABEL_SHEET.headerMm - CUSTOM_LABEL_SHEET.marginMm * 2;
  const printedWidth = labelWidth + bleedMm * 2;
  const printedHeight = labelHeight + bleedMm * 2;
  const columns = Math.max(0, Math.floor((printableWidth + gapMm) / (printedWidth + gapMm)));
  const rows = Math.max(0, Math.floor((printableHeight + gapMm) / (printedHeight + gapMm)));
  return {
    orientation: rotated ? "rotated" : "normal",
    columns,
    rows,
    capacityPerSheet: columns * rows,
    printedWidth,
    printedHeight,
    usedWidthMm: columns ? columns * printedWidth + (columns - 1) * gapMm : 0,
    usedHeightMm: rows ? rows * printedHeight + (rows - 1) * gapMm : 0,
    printableWidth,
    printableHeight,
  };
}

export function calculateCustomLabelSheetLayout(input = {}) {
  const draft = createCustomLabelBetaDraft(input);
  const normal = orientationLayout(draft, false);
  const rotated = orientationLayout(draft, true);
  const chosen = rotated.capacityPerSheet > normal.capacityPerSheet ? rotated : normal;
  const capacityPerSheet = Math.max(1, chosen.capacityPerSheet);
  const sheetCount = Math.ceil(draft.quantity / capacityPerSheet);
  return {
    ...chosen,
    sheetCount,
    requestedQuantity: draft.quantity,
    totalCapacity: capacityPerSheet * sheetCount,
    waste: capacityPerSheet * sheetCount - draft.quantity,
  };
}

export function calculateCustomLabelBetaPrice(input = {}, layoutInput) {
  const draft = createCustomLabelBetaDraft(input);
  const layout = layoutInput || calculateCustomLabelSheetLayout(draft);
  const labelAreaM2 = (draft.widthMm * draft.heightMm * draft.quantity) / 1_000_000;
  const setupNok = 59;
  const sheetNok = layout.sheetCount * 34;
  const materialNok = labelAreaM2 * 520;
  const handlingNok = draft.quantity * 0.38;
  const motifNok = draft.motifMode === "upload" ? 29 : 0;
  const shapeNok = draft.shape === "custom" ? 35 : 0;
  const totalNok = Math.max(129, Math.round(setupNok + sheetNok + materialNok + handlingNok + motifNok + shapeNok));
  return {
    currency: "NOK",
    status: "estimate",
    setupNok,
    sheetNok,
    materialNok: Math.round(materialNok),
    handlingNok: Math.round(handlingNok),
    motifNok,
    shapeNok,
    totalNok,
    unitNok: Number((totalNok / draft.quantity).toFixed(2)),
  };
}

function longestLine(draft) {
  return [draft.primaryText, draft.secondaryText, draft.thirdText]
    .map((line) => String(line || "").trim())
    .sort((a, b) => b.length - a.length)[0] || "";
}

export function calculateCustomLabelTextFit(input = {}) {
  const draft = createCustomLabelBetaDraft(input);
  const lines = [draft.primaryText, draft.secondaryText, draft.thirdText].filter((line) => String(line || "").trim());
  const motifFraction = draft.motifMode === "none" ? 0 : 0.28;
  const usableWidth = Math.max(2, draft.widthMm * (0.88 - motifFraction));
  const usableHeight = Math.max(2, draft.heightMm * 0.72);
  const byHeight = usableHeight / Math.max(1, lines.length) / 1.15;
  const byWidth = usableWidth / Math.max(1, longestLine(draft).length * 0.56);
  const fontSizeMm = Math.min(draft.heightMm * 0.36, byHeight, byWidth);
  return {
    fontSizeMm: Number(fontSizeMm.toFixed(2)),
    fontSizePt: Number((fontSizeMm * 2.83465).toFixed(1)),
    minimumPrintSizeMm: 2.2,
    isPrintSafe: fontSizeMm >= 2.2,
    lineCount: lines.length,
    usableWidthMm: usableWidth,
  };
}

export function validateCustomLabelBetaDraft(input = {}) {
  const draft = createCustomLabelBetaDraft(input);
  const layout = calculateCustomLabelSheetLayout(draft);
  const textFit = calculateCustomLabelTextFit(draft);
  const errors = [];
  const warnings = [];
  if (!String(draft.primaryText || "").trim()) errors.push("Skriv inn minst én tekstlinje.");
  if (layout.columns < 1 || layout.rows < 1) errors.push("Formatet er for stort for produksjonsarket.");
  if (!textFit.isPrintSafe) errors.push("Teksten blir for liten for sikker og tydelig produksjon. Kort ned teksten eller velg en større lapp.");
  if (draft.motifMode === "upload" && !draft.uploadedImage?.dataUrl) warnings.push("Last opp et motiv før denne varianten kan ferdigstilles.");
  if (layout.waste / Math.max(1, layout.totalCapacity) > 0.35) warnings.push("Valgt antall utnytter arket dårlig. Et av de foreslåtte antallene gir lavere svinn.");
  if (draft.quantity > layout.capacityPerSheet * 5) warnings.push("Bestillingen bruker mange ark. Pris og produksjonstid bør kontrolleres før publisering.");
  return { valid: errors.length === 0, errors, warnings, layout, textFit };
}
